import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiFetch } from '../utils/httpClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faPalette, 
  faUsers,
  faBuilding,
  faChevronRight,
  faChevronLeft,
  faCheck,
  faTimes,
  faEdit,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { ChangePasswordModal } from '../components/ChangePasswordModal';
import { UserManagementSection } from '../components/UserManagementSection';
import { OrganizationManagementSection } from '../components/OrganizationManagementSection';

const PageContainer = styled.div`
  min-height: 100vh;
  min-height: 100dvh; /* Use dynamic viewport height for mobile */
  display: flex;
  background: var(--color-background);
  color: var(--color-text-primary);
  overflow-x: hidden;
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    flex-direction: column;
    overflow-x: hidden;
  }
`;

const SettingsSidebar = styled.div`
  width: 18rem; /* 288px in rem - wider to fit "User Management" */
  background: var(--color-surface-alt);
  border-right: 1px solid var(--color-border);
  padding: var(--spacing-6);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  position: sticky;
  top: 0;
  height: 100vh;
  height: 100dvh; /* Use dynamic viewport height for mobile */
  overflow-y: auto;
  box-sizing: border-box;
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    width: 16rem; /* 256px in rem */
    padding: var(--spacing-4);
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    display: none;
  }
`;

const MobileTabsContainer = styled.div`
  display: none;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    display: block;
    width: 100%;
    margin-bottom: var(--spacing-4);
    padding-bottom: var(--spacing-2);
  }
`;

const MobileTabsScroll = styled.div`
  display: flex;
  gap: var(--spacing-2);
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
  
  /* Add padding to first and last items for better scroll UX */
  padding: 0 var(--spacing-4);
  margin: 0 calc(-1 * var(--spacing-4));
`;

const MobileTabBase = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-2) var(--spacing-4);
  border-radius: var(--radius-md);
  border: 1px solid ${props => props.$isActive ? 'var(--color-accent)' : 'var(--color-border)'};
  background: ${props => props.$isActive ? 'var(--color-accent)' : 'var(--color-surface)'};
  color: ${props => props.$isActive ? '#ffffff' : 'var(--color-text-primary)'};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: ${props => props.$isActive ? '600' : '500'};
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$isActive ? 'var(--color-accent-light)' : 'var(--color-surface-alt)'};
    border-color: var(--color-accent);
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  svg {
    font-size: 0.875rem;
  }
`;

interface MobileTabProps {
  $isActive: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const MobileTab = React.forwardRef<HTMLButtonElement, MobileTabProps>(
  ({ $isActive, onClick, children }, ref) => (
    <MobileTabBase ref={ref} $isActive={$isActive} onClick={onClick} type="button">
      {children}
    </MobileTabBase>
  )
);

MobileTab.displayName = 'MobileTab';

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  margin-bottom: var(--spacing-6);
`;

const SidebarBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    background: var(--color-surface-alt);
    border-color: var(--color-accent);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;


const SidebarTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: var(--color-text-primary);
  flex: 1;
`;

const SidebarSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
`;

const SidebarSectionTitle = styled.h3`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-2);
  font-weight: 600;
`;

const SidebarButton = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-3);
  padding: var(--spacing-3) var(--spacing-4);
  background: ${props => props.$isActive ? 'var(--color-surface)' : 'transparent'};
  color: var(--color-text-primary);
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.95rem;
  text-align: left;
  transition: all 0.2s ease;
  white-space: nowrap;
  width: 100%;
  
  & > div {
    display: flex;
    align-items: center;
    gap: var(--spacing-3);
    flex: 1;
    white-space: nowrap;
  }
  
  & > div > span {
    white-space: nowrap;
  }
  
  svg:last-child {
    flex-shrink: 0;
  }
  
  &:hover {
    background: var(--color-surface);
  }
  
  ${props => props.$isActive && `
    border-left: 3px solid var(--color-accent);
    font-weight: 600;
  `}
`;

const SettingsContent = styled.div<{ $isFullWidth?: boolean }>`
  flex: 1;
  padding: clamp(var(--spacing-4), 4vw, var(--spacing-8));
  max-width: ${props => props.$isFullWidth ? 'none' : '56.25rem'}; /* 900px in rem */
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    padding: var(--spacing-6);
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: var(--spacing-3) var(--spacing-4);
    margin-left: 0; /* Remove margin when sidebar is hidden */
    max-width: 100%;
  }
`;

const Section = styled.section`
  margin-bottom: var(--spacing-10);
  width: 100%;
  min-width: 0;
  overflow-x: hidden;
  max-width: 100%;
  margin-left: 0;
  position: relative;
  box-sizing: border-box;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    margin-bottom: var(--spacing-6);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-2);
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-surface-alt);
    border-color: var(--color-accent);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const SectionTitle = styled.h1`
  font-size: clamp(1.5rem, 5vw, 2rem);
  font-weight: 600;
  margin: 0;
  color: var(--color-text-primary);
  text-align: left;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    font-size: 1.5rem;
    margin-bottom: var(--spacing-2);
  }
`;

const SectionDescription = styled.p`
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-8);
  font-size: 0.95rem;
  text-align: left;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    font-size: 0.875rem;
    margin-bottom: var(--spacing-4);
  }
`;

const SettingGroup = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--spacing-6);
  margin-bottom: var(--spacing-4);
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: var(--spacing-4);
    border-radius: var(--radius-md);
  }
`;

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-4) 0;
  border-bottom: 1px solid var(--color-border);
  gap: var(--spacing-3);
  min-width: 0;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: var(--spacing-3) 0;
    gap: var(--spacing-2);
    flex-wrap: nowrap;
  }
`;

const SettingLabel = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 60%;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    max-width: 50%;
    flex-shrink: 1;
  }
`;

const SettingTitle = styled.h3`
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: var(--spacing-1);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    font-size: 0.875rem;
  }
`;

const SettingDescription = styled.p`
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    font-size: 0.75rem;
  }
`;

const SettingControl = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  flex-shrink: 0;
  min-width: fit-content;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    gap: var(--spacing-2);
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: var(--spacing-4);
  flex-wrap: wrap;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    gap: var(--spacing-2);
    width: 100%;
  }
`;

const RadioOption = styled.label<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-2) var(--spacing-4);
  border: none !important;
  border-radius: var(--radius-md);
  cursor: pointer;
  background: ${props => props.$isSelected ? 'rgba(var(--color-accent-rgb), 0.1)' : 'transparent'};
  transition: all 0.2s ease;
  outline: none !important;
  white-space: nowrap;
  flex-shrink: 0;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: var(--spacing-2) var(--spacing-3);
    font-size: 0.875rem;
    flex: 1;
    min-width: 0;
    justify-content: center;
  }
  
  &:hover {
    background: ${props => props.$isSelected ? 'rgba(var(--color-accent-rgb), 0.15)' : 'rgba(var(--color-accent-rgb), 0.05)'};
  }
  
  &:focus,
  &:focus-within {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  input {
    margin: 0;
    cursor: pointer;
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
    
    &:focus {
      outline: none !important;
      border: none !important;
      box-shadow: none !important;
    }
    
    &[type="radio"] {
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      width: 1rem;
      height: 1rem;
      border: 2px solid var(--color-border);
      border-radius: 50%;
      background: transparent;
      position: relative;
      
      &:checked {
        border-color: var(--color-accent);
        background: var(--color-accent);
        
        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 0.375rem;
          height: 0.375rem;
          border-radius: 50%;
          background: #fff;
        }
      }
    }
  }
`;

const InfoText = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    font-size: 0.8125rem;
    max-width: 200px;
  }
`;

const Input = styled.input`
  padding: var(--spacing-2) var(--spacing-3);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 0.875rem;
  min-width: 150px;
  max-width: 300px;
  box-sizing: border-box;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    min-width: 120px;
    max-width: 200px;
    font-size: 0.8125rem;
    padding: var(--spacing-2);
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Badge = styled.span<{ $variant?: 'success' | 'warning' | 'error' }>`
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    switch (props.$variant) {
      case 'success': return 'rgba(74, 222, 128, 0.2)';
      case 'warning': return 'rgba(250, 204, 21, 0.2)';
      case 'error': return 'rgba(239, 68, 68, 0.2)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.$variant) {
      case 'success': return '#4ade80';
      case 'warning': return '#facc15';
      case 'error': return '#ef4444';
      default: return 'var(--color-text-secondary)';
    }
  }};
`;

const Button = styled.button<{ $variant?: 'primary' | 'danger' | 'secondary' }>`
  padding: var(--spacing-3) var(--spacing-5);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: var(--spacing-2) var(--spacing-3);
    font-size: 0.8125rem;
  }
  
  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: var(--color-accent);
          color: #fff;
          border-color: var(--color-accent);
          &:hover {
            background: var(--color-accent-light);
          }
        `;
      case 'danger':
        return `
          background: transparent;
          color: var(--color-error);
          border-color: var(--color-error);
          &:hover {
            background: rgba(var(--color-error-rgb), 0.1);
          }
        `;
      default:
        return `
          background: var(--color-surface);
          color: var(--color-text-primary);
          &:hover {
            background: var(--color-surface-alt);
          }
        `;
    }
  }}
`;


type SettingsSection = 'account' | 'appearance' | 'users' | 'organizations';

interface SettingsPageProps {
  onBack?: () => void;
}

export const SettingsPage: React.FC<SettingsPageProps> = ({ onBack }) => {
  const { user, refreshUser } = useAuth();
  const { theme, setTheme, systemTheme } = useTheme();
  const [activeSection, setActiveSection] = useState<SettingsSection>('account');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [editingUsername, setEditingUsername] = useState(false);
  const [usernameValue, setUsernameValue] = useState(user?.username || '');
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [updatingUsername, setUpdatingUsername] = useState(false);
  
  // Refs for mobile tabs to enable auto-scrolling
  const accountTabRef = useRef<HTMLButtonElement | null>(null);
  const appearanceTabRef = useRef<HTMLButtonElement | null>(null);
  const usersTabRef = useRef<HTMLButtonElement | null>(null);
  const organizationsTabRef = useRef<HTMLButtonElement | null>(null);
  const tabsScrollRef = useRef<HTMLDivElement | null>(null);

  // Update usernameValue when user changes
  useEffect(() => {
    setUsernameValue(user?.username || '');
  }, [user?.username]);

  const handleUpdateUsername = async () => {
    if (!usernameValue.trim()) {
      setUsernameError('Username cannot be empty');
      return;
    }

    setUpdatingUsername(true);
    setUsernameError(null);

    try {
      const response = await apiFetch('/api/v1/auth/me/username', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: usernameValue.trim() }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.detail || 'Failed to update username');
      }

      // Refresh user data
      await refreshUser();
      setEditingUsername(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err) {
      setUsernameError(err instanceof Error ? err.message : 'Failed to update username');
    } finally {
      setUpdatingUsername(false);
    }
  };
  const [themePreference, setThemePreference] = useState<'light' | 'dark' | 'system'>(() => {
    const saved = localStorage.getItem('themePreference') as 'light' | 'dark' | 'system' | null;
    // If no preference saved, check if current theme matches system theme
    if (!saved) {
      const currentTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const systemTheme = systemPrefersDark ? 'dark' : 'light';
      // If current theme matches system, assume user wants system preference
      if (currentTheme === systemTheme) {
        return 'system';
      }
      return currentTheme || 'dark';
    }
    return saved;
  });

  // Load saved preferences
  useEffect(() => {
    const savedFontSize = localStorage.getItem('fontSize') as 'small' | 'medium' | 'large' | null;
    if (savedFontSize) {
      setFontSize(savedFontSize);
    }
  }, []);

  // Apply font size to document
  useEffect(() => {
    document.documentElement.setAttribute('data-font-size', fontSize);
    localStorage.setItem('fontSize', fontSize);
  }, [fontSize]);

  // Track theme preference and apply system theme when needed
  useEffect(() => {
    if (themePreference === 'system') {
      setTheme(systemTheme);
      // Listen for system theme changes
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        setTheme(e.matches ? 'dark' : 'light');
      };
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      setTheme(themePreference);
    }
  }, [themePreference, systemTheme, setTheme]);

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setThemePreference(newTheme);
    localStorage.setItem('themePreference', newTheme);
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus('idle'), 2000);
  };

  const handleFontSizeChange = (size: 'small' | 'medium' | 'large') => {
    setFontSize(size);
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus('idle'), 2000);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isSystemAdmin = user?.role === 'admin' || user?.role === 'system_admin';
  const isOrgAdmin = user?.organization_role === 'admin' || user?.organization_role === 'owner';
  const canManageUsers = isSystemAdmin;
  const canManageOrganizations = isSystemAdmin;

  // Auto-scroll to center the active tab on mobile
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Only run on mobile screens
    if (window.innerWidth > 480) return;
    
    let activeTabRef: React.RefObject<HTMLButtonElement | null> | null = null;
    
    switch (activeSection) {
      case 'account':
        activeTabRef = accountTabRef;
        break;
      case 'appearance':
        activeTabRef = appearanceTabRef;
        break;
      case 'users':
        activeTabRef = usersTabRef;
        break;
      case 'organizations':
        activeTabRef = organizationsTabRef;
        break;
    }
    
    if (activeTabRef?.current && tabsScrollRef.current) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        const tabElement = activeTabRef!.current;
        const scrollContainer = tabsScrollRef.current;
        
        if (tabElement && scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const tabRect = tabElement.getBoundingClientRect();
          
          // Calculate the position to center the tab
          const scrollLeft = scrollContainer.scrollLeft;
          const tabLeft = tabRect.left - containerRect.left + scrollLeft;
          const tabWidth = tabRect.width;
          const containerWidth = containerRect.width;
          
          // Center the tab
          const targetScroll = tabLeft - (containerWidth / 2) + (tabWidth / 2);
          
          scrollContainer.scrollTo({
            left: targetScroll,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [activeSection]);


  return (
    <PageContainer>
      <SettingsSidebar>
        <SidebarHeader>
          {onBack && (
            <SidebarBackButton onClick={onBack} type="button" aria-label="Go back">
              <FontAwesomeIcon icon={faChevronLeft} />
            </SidebarBackButton>
          )}
          <SidebarTitle>Settings</SidebarTitle>
        </SidebarHeader>
        
        <SidebarSection>
          <SidebarButton
            $isActive={activeSection === 'account'}
            onClick={() => setActiveSection('account')}
            type="button"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', whiteSpace: 'nowrap' }}>
              <FontAwesomeIcon icon={faUser} style={{ flexShrink: 0 }} />
              <span>Account</span>
            </div>
            {activeSection === 'account' && <FontAwesomeIcon icon={faChevronRight} style={{ flexShrink: 0 }} />}
          </SidebarButton>
          
          <SidebarButton
            $isActive={activeSection === 'appearance'}
            onClick={() => setActiveSection('appearance')}
            type="button"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', whiteSpace: 'nowrap' }}>
              <FontAwesomeIcon icon={faPalette} style={{ flexShrink: 0 }} />
              <span>Appearance</span>
            </div>
            {activeSection === 'appearance' && <FontAwesomeIcon icon={faChevronRight} style={{ flexShrink: 0 }} />}
          </SidebarButton>
          
          {canManageUsers && (
            <SidebarButton
              $isActive={activeSection === 'users'}
              onClick={() => setActiveSection('users')}
              type="button"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', whiteSpace: 'nowrap' }}>
                <FontAwesomeIcon icon={faUsers} style={{ flexShrink: 0 }} />
                <span>User Management</span>
              </div>
              {activeSection === 'users' && <FontAwesomeIcon icon={faChevronRight} style={{ flexShrink: 0 }} />}
            </SidebarButton>
          )}
          
          {canManageOrganizations && (
            <SidebarButton
              $isActive={activeSection === 'organizations'}
              onClick={() => setActiveSection('organizations')}
              type="button"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', whiteSpace: 'nowrap' }}>
                <FontAwesomeIcon icon={faBuilding} style={{ flexShrink: 0 }} />
                <span>Organizations</span>
              </div>
              {activeSection === 'organizations' && <FontAwesomeIcon icon={faChevronRight} style={{ flexShrink: 0 }} />}
            </SidebarButton>
          )}
        </SidebarSection>
      </SettingsSidebar>

      <SettingsContent $isFullWidth={activeSection === 'users' || activeSection === 'organizations'}>
        <MobileTabsContainer>
          <MobileTabsScroll ref={tabsScrollRef}>
            <MobileTab
              ref={accountTabRef}
              $isActive={activeSection === 'account'}
              onClick={() => setActiveSection('account')}
            >
              <FontAwesomeIcon icon={faUser} />
              <span>Account</span>
            </MobileTab>
            
            <MobileTab
              ref={appearanceTabRef}
              $isActive={activeSection === 'appearance'}
              onClick={() => setActiveSection('appearance')}
            >
              <FontAwesomeIcon icon={faPalette} />
              <span>Appearance</span>
            </MobileTab>
            
            {canManageUsers && (
              <MobileTab
                ref={usersTabRef}
                $isActive={activeSection === 'users'}
                onClick={() => setActiveSection('users')}
              >
                <FontAwesomeIcon icon={faUsers} />
                <span>Users</span>
              </MobileTab>
            )}
            
            {canManageOrganizations && (
              <MobileTab
                ref={organizationsTabRef}
                $isActive={activeSection === 'organizations'}
                onClick={() => setActiveSection('organizations')}
              >
                <FontAwesomeIcon icon={faBuilding} />
                <span>Organizations</span>
              </MobileTab>
            )}
          </MobileTabsScroll>
        </MobileTabsContainer>
        
        {activeSection === 'account' && (
          <Section>
            <SectionTitle>Account</SectionTitle>
            <SectionDescription>Manage your account settings and security</SectionDescription>

            <SettingGroup>
              <SettingRow>
                <SettingLabel>
                  <SettingTitle>Email Address</SettingTitle>
                  <SettingDescription>Your account email address</SettingDescription>
                </SettingLabel>
                <SettingControl>
                  <InfoText>{user?.email}</InfoText>
                </SettingControl>
              </SettingRow>

              <SettingRow>
                <SettingLabel>
                  <SettingTitle>Username</SettingTitle>
                  <SettingDescription>Your display username</SettingDescription>
                </SettingLabel>
                <SettingControl>
                  {editingUsername ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
                      <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
                        <Input
                          type="text"
                          value={usernameValue}
                          onChange={(e) => {
                            setUsernameValue(e.target.value);
                            setUsernameError(null);
                          }}
                          disabled={updatingUsername}
                          placeholder="Enter username"
                        />
                        <Button
                          onClick={handleUpdateUsername}
                          disabled={updatingUsername}
                          $variant="primary"
                          style={{ minWidth: '80px' }}
                        >
                          {updatingUsername ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            'Save'
                          )}
                        </Button>
                        <Button
                          onClick={() => {
                            setEditingUsername(false);
                            setUsernameValue(user?.username || '');
                            setUsernameError(null);
                          }}
                          disabled={updatingUsername}
                        >
                          Cancel
                        </Button>
                      </div>
                      {usernameError && (
                        <div style={{ color: 'var(--color-error)', fontSize: '0.875rem' }}>
                          {usernameError}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
                      <InfoText>{user?.username || 'Not set'}</InfoText>
                      <Button
                        onClick={() => setEditingUsername(true)}
                        style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                    </div>
                  )}
                </SettingControl>
              </SettingRow>

              <SettingRow>
                <SettingLabel>
                  <SettingTitle>Email Verification</SettingTitle>
                  <SettingDescription>Email verification status</SettingDescription>
                </SettingLabel>
                <SettingControl>
                  {user?.email_verified ? (
                    <Badge $variant="success">
                      <FontAwesomeIcon icon={faCheck} style={{ marginRight: '0.25rem' }} />
                      Verified
                    </Badge>
                  ) : (
                    <Badge $variant="warning">
                      <FontAwesomeIcon icon={faTimes} style={{ marginRight: '0.25rem' }} />
                      Not Verified
                    </Badge>
                  )}
                </SettingControl>
              </SettingRow>

              <SettingRow>
                <SettingLabel>
                  <SettingTitle>Account Created</SettingTitle>
                  <SettingDescription>When your account was created</SettingDescription>
                </SettingLabel>
                <SettingControl>
                  <InfoText>{formatDate(user?.created_at)}</InfoText>
                </SettingControl>
              </SettingRow>

              <SettingRow>
                <SettingLabel>
                  <SettingTitle>Last Login</SettingTitle>
                  <SettingDescription>Your last login time</SettingDescription>
                </SettingLabel>
                <SettingControl>
                  <InfoText>{formatDate(user?.last_login_at)}</InfoText>
                </SettingControl>
              </SettingRow>

              <SettingRow>
                <SettingLabel>
                  <SettingTitle>Password</SettingTitle>
                  <SettingDescription>Change your account password</SettingDescription>
                </SettingLabel>
                <SettingControl>
                  <Button onClick={() => setShowChangePassword(true)}>
                    Change Password
                  </Button>
                </SettingControl>
              </SettingRow>
            </SettingGroup>
          </Section>
        )}

        {activeSection === 'appearance' && (
          <Section>
            <SectionTitle>Appearance</SectionTitle>
            <SectionDescription>Customize the look and feel of the application</SectionDescription>

            <SettingGroup>
              <SettingRow>
                <SettingLabel>
                  <SettingTitle>Theme</SettingTitle>
                  <SettingDescription>Choose your preferred color theme</SettingDescription>
                </SettingLabel>
                <SettingControl>
                  <RadioGroup>
                    <RadioOption $isSelected={themePreference === 'light'}>
                      <input
                        type="radio"
                        name="theme"
                        value="light"
                        checked={themePreference === 'light'}
                        onChange={() => handleThemeChange('light')}
                      />
                      Light
                    </RadioOption>
                    <RadioOption $isSelected={themePreference === 'dark'}>
                      <input
                        type="radio"
                        name="theme"
                        value="dark"
                        checked={themePreference === 'dark'}
                        onChange={() => handleThemeChange('dark')}
                      />
                      Dark
                    </RadioOption>
                    <RadioOption $isSelected={themePreference === 'system'}>
                      <input
                        type="radio"
                        name="theme"
                        value="system"
                        checked={themePreference === 'system'}
                        onChange={() => handleThemeChange('system')}
                      />
                      System
                    </RadioOption>
                  </RadioGroup>
                </SettingControl>
              </SettingRow>

              <SettingRow>
                <SettingLabel>
                  <SettingTitle>Font Size</SettingTitle>
                  <SettingDescription>Adjust the text size throughout the application</SettingDescription>
                </SettingLabel>
                <SettingControl>
                  <RadioGroup>
                    <RadioOption $isSelected={fontSize === 'small'}>
                      <input
                        type="radio"
                        name="fontSize"
                        value="small"
                        checked={fontSize === 'small'}
                        onChange={() => handleFontSizeChange('small')}
                      />
                      Small
                    </RadioOption>
                    <RadioOption $isSelected={fontSize === 'medium'}>
                      <input
                        type="radio"
                        name="fontSize"
                        value="medium"
                        checked={fontSize === 'medium'}
                        onChange={() => handleFontSizeChange('medium')}
                      />
                      Medium
                    </RadioOption>
                    <RadioOption $isSelected={fontSize === 'large'}>
                      <input
                        type="radio"
                        name="fontSize"
                        value="large"
                        checked={fontSize === 'large'}
                        onChange={() => handleFontSizeChange('large')}
                      />
                      Large
                    </RadioOption>
                  </RadioGroup>
                </SettingControl>
              </SettingRow>
            </SettingGroup>

            {saveStatus === 'saved' && (
              <div style={{ 
                marginTop: 'var(--spacing-4)', 
                color: 'var(--color-success)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)'
              }}>
                <FontAwesomeIcon icon={faCheck} />
                Settings saved
              </div>
            )}
          </Section>
        )}

        {activeSection === 'users' && canManageUsers && (
          <UserManagementSection />
        )}

        {activeSection === 'organizations' && canManageOrganizations && (
          <OrganizationManagementSection />
        )}
      </SettingsContent>

      {showChangePassword && (
        <ChangePasswordModal
          isOpen={showChangePassword}
          onClose={() => setShowChangePassword(false)}
          onSuccess={() => {
            setShowChangePassword(false);
            // Just refresh user data, don't redirect
            refreshUser().catch(() => {
              // Ignore errors - user is still logged in
            });
          }}
        />
      )}
    </PageContainer>
  );
};

